import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { UrlApi } from "../../../../../../../../services/apirest";

const formatMoneda = (valor) =>
  new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
  }).format(Number(valor) || 0);

// Tarea #871: créditos QR (Mercado Pago) de pedidos anulados/descartados de envasado que ya
// se habían pagado - la plata quedó a favor del cliente. Se elige uno o varios para aplicarlos
// como instrumento de pago del recibo.
export default function SeleccionCreditosQr({
  open,
  onClose,
  onConfirmar,
  idCliente,
  idsExcluidos = [],
}) {
  const [creditos, setCreditos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [seleccionados, setSeleccionados] = useState([]);

  const excluidos = useMemo(
    () => new Set((idsExcluidos || []).map((id) => String(id))),
    [idsExcluidos],
  );

  const cargar = async () => {
    if (!idCliente) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${UrlApi}/envasado_pagos_combinados/creditos-qr/${idCliente}`);
      const data = await res.json();
      setCreditos(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "No se pudieron cargar los créditos QR disponibles");
      setCreditos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setSeleccionados([]);
      setError("");
      return;
    }
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idCliente]);

  const disponibles = useMemo(
    () => creditos.filter((c) => !excluidos.has(String(c.id))),
    [creditos, excluidos],
  );

  const estaSeleccionado = (id) =>
    seleccionados.some((c) => String(c.id) === String(id));

  const toggle = (credito) => {
    setSeleccionados((prev) =>
      prev.some((c) => String(c.id) === String(credito.id))
        ? prev.filter((c) => String(c.id) !== String(credito.id))
        : [...prev, credito],
    );
  };

  const importeSeleccionado = seleccionados.reduce(
    (acc, c) => acc + (Number(c.importe) || 0),
    0,
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Créditos QR disponibles</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Pedidos pagados con QR (Mercado Pago) cuyo pedido fue anulado — la plata quedó a favor
          del cliente. Elegí uno o varios para aplicarlos a este recibo.
        </Alert>

        {error ? (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={cargar}>
                Reintentar
              </Button>
            }
          >
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 320, border: "1px solid #e0e0e0", borderRadius: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ bgcolor: "#f5f5f5" }} />
                    {["Pagado con", "Referencia", "Importe", "Pedido", "Dirección"].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 600, bgcolor: "#f5f5f5" }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {disponibles.map((credito) => (
                    <TableRow
                      key={credito.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => toggle(credito)}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={estaSeleccionado(credito.id)}
                          onChange={() => toggle(credito)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                      <TableCell>Mercado Pago</TableCell>
                      <TableCell>{credito.referencia || credito.detalle || "—"}</TableCell>
                      <TableCell>{formatMoneda(credito.importe)}</TableCell>
                      <TableCell>{credito.pedido ?? "—"}</TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
                          {credito.direccion_entrega || "—"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {disponibles.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: "center" }}>
                Este cliente no tiene créditos QR disponibles.
              </Typography>
            ) : null}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Typography variant="body2" sx={{ mr: "auto" }}>
          {seleccionados.length} seleccionado{seleccionados.length === 1 ? "" : "s"}
          {seleccionados.length > 0 ? ` · ${formatMoneda(importeSeleccionado)}` : ""}
        </Typography>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          disabled={seleccionados.length === 0}
          onClick={() => onConfirmar?.(seleccionados)}
        >
          Agregar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
